/**
 * Utility functions for persisting column visibility settings
 * Settings are stored per user per project per table in the database
 */

import { ApiRequest, postItem } from './ApiUtils';

const API_ENDPOINT = '/UserGridPreference';

// In-memory cache for pending saves (debounce)
const pendingSaves = {};

// In-memory cache for loaded visibility (to avoid repeated API calls)
const visibilityCache = {};

// Helper to check if a string is a valid GUID
const isValidGuid = (str) => {
  if (!str || str === '@global') return false;
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return guidRegex.test(str);
};

// Helper to get cache key
const getCacheKey = (projectId, gridTitle) => {
  return projectId + '_' + gridTitle;
};

/**
 * Save column visibility settings to database
 */
export const saveColumnVisibility = (systemUserId, projectId, gridTitle, columnVisibility) => {
  try {
    // Only save to DB if we have valid GUIDs for user and project
    if (!isValidGuid(systemUserId) || !isValidGuid(projectId)) {
      console.log('Skipping DB save - invalid systemUserId or projectId:', systemUserId, projectId);
      return;
    }

    // Update in-memory cache immediately
    const cacheKey = getCacheKey(projectId, gridTitle);
    visibilityCache[cacheKey] = columnVisibility;

    // Debounce DB saves
    if (pendingSaves[cacheKey]) {
      clearTimeout(pendingSaves[cacheKey]);
    }

    pendingSaves[cacheKey] = setTimeout(async () => {
      try {
        const data = {
          projectId: projectId,
          gridName: gridTitle,
          columnVisibility: JSON.stringify(columnVisibility)
        };
        const result = await postItem(API_ENDPOINT, '', data, false, true);
        if (result && result.success) {
          console.log('Column visibility saved to database for', gridTitle);
        } else {
          console.error('Failed to save column visibility to database:', result);
        }
      } catch (error) {
        console.error('Error saving column visibility to database:', error);
      }
      delete pendingSaves[cacheKey];
    }, 2000);
  } catch (error) {
    console.error('Error saving column visibility:', error);
  }
};

/**
 * Load column visibility from in-memory cache (sync)
 * Returns cached value or null if not loaded yet
 */
export const loadColumnVisibility = (systemUserId, projectId, gridTitle) => {
  const cacheKey = getCacheKey(projectId, gridTitle);
  return visibilityCache[cacheKey] || null;
};

/**
 * Load column visibility settings from database (async)
 */
export const loadColumnVisibilityFromDb = async (systemUserId, projectId, gridTitle) => {
  try {
    // Only load from DB if we have valid GUIDs
    if (!isValidGuid(systemUserId) || !isValidGuid(projectId)) {
      console.log('Skipping DB load - invalid systemUserId or projectId:', systemUserId, projectId);
      return null;
    }

    const params = '?projectId=' + projectId + '&gridName=' + encodeURIComponent(gridTitle);
    const response = await ApiRequest(API_ENDPOINT, params);

    if (response && response.success && response.columnVisibility) {
      const visibility = JSON.parse(response.columnVisibility);
      // Update in-memory cache
      const cacheKey = getCacheKey(projectId, gridTitle);
      visibilityCache[cacheKey] = visibility;
      console.log('Column visibility loaded from database for', gridTitle);
      return visibility;
    }
    return null;
  } catch (error) {
    console.error('Error loading column visibility from database:', error);
    return null;
  }
};

/**
 * Clear column visibility from cache
 */
export const clearColumnVisibility = (systemUserId, projectId, gridTitle) => {
  const cacheKey = getCacheKey(projectId, gridTitle);
  delete visibilityCache[cacheKey];
};
